import { useEffect, useMemo, useState } from 'react';
import { Loader2, Trash2, QrCode, Plus, Search, ExternalLink } from 'lucide-react';
import { api } from '../../lib/api';
import type { QrCodeRecord, QrStyle } from '../../lib/types';
import ContextCard from '../../components/ui/ContextCard';
import Modal from '../../components/ui/Modal';
import QrCodeModal from '../../components/ui/QrCodeModal';
import { Field, Input } from '../../components/ui/Field';
import PageContainer from '../../components/ui/PageContainer';
import PageHeader from '../../components/ui/PageHeader';

const MODULE_LABELS: Record<string, string> = {
  reputation: 'Reputación',
  linkinbio: 'Página de enlaces',
  custom: 'General',
};

function moduleLabel(module: string): string {
  return MODULE_LABELS[module] ?? module.charAt(0).toUpperCase() + module.slice(1);
}

function parseStyle(json: string): QrStyle | undefined {
  try {
    const s = JSON.parse(json);
    if (s && typeof s === 'object' && s.shape && s.color && s.logoSource) return s as QrStyle;
  } catch { /* estilo por defecto */ }
  return undefined;
}

function formatDate(value: Date | string | null | undefined): string {
  if (!value) return '';
  return new Date(value).toLocaleDateString('es-MX', { dateStyle: 'medium' });
}

export default function QrCodesPage() {
  const [items, setItems] = useState<QrCodeRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<QrCodeRecord | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState<{ label: string; value: string } | null>(null);

  function load() {
    setLoading(true);
    api.qr.list().then(setItems).catch(() => { /* sin códigos aún */ }).finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  async function handleDelete(item: QrCodeRecord) {
    if (!confirm(`¿Eliminar el código QR de "${item.label}"? El enlace seguirá funcionando, solo se borra esta imagen guardada.`)) return;
    await api.qr.remove(item.id);
    setItems((prev) => prev.filter((q) => q.id !== item.id));
  }

  const modules = useMemo(() => [...new Set(items.map((i) => i.module))].sort(), [items]);

  const filtered = items.filter((item) => {
    if (moduleFilter !== 'all' && item.module !== moduleFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return item.label.toLowerCase().includes(q) || item.targetUrl.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <PageContainer>
      <PageHeader title="Códigos QR">
        <button
          type="button"
          onClick={() => { setDraft({ label: '', value: '' }); setCreateOpen(true); }}
          className="inline-flex items-center gap-2 rounded-md bg-ui-accent px-4 py-2 text-sm font-medium text-ui-bg hover:bg-ui-accent-hover"
        >
          <Plus size={16} /> Nuevo código QR
        </button>
      </PageHeader>

      <ContextCard
        title="¿Qué encuentras aquí?"
        description="Todos los códigos QR que has generado en el panel, reunidos en un solo lugar."
        detail="Cada código lleva a un enlace de tu negocio. Puedes volver a abrirlo para guardarlo o compartirlo, cambiar su apariencia, o eliminarlo cuando ya no lo necesites."
      />

      {/* Filtros */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setModuleFilter('all')}
            className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
              moduleFilter === 'all'
                ? 'bg-ui-accent text-ui-bg'
                : 'bg-ui-surface text-ui-text-muted hover:text-ui-text hover:bg-ui-surface-2'
            }`}
          >
            Todos {items.length > 0 && <span className="opacity-60">({items.length})</span>}
          </button>
          {modules.map((m) => {
            const count = items.filter((i) => i.module === m).length;
            return (
              <button
                key={m}
                type="button"
                onClick={() => setModuleFilter(m)}
                className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                  moduleFilter === m
                    ? 'bg-ui-accent text-ui-bg'
                    : 'bg-ui-surface text-ui-text-muted hover:text-ui-text hover:bg-ui-surface-2'
                }`}
              >
                {moduleLabel(m)} <span className="opacity-60">({count})</span>
              </button>
            );
          })}
        </div>
        <div className="relative sm:ml-auto sm:w-64">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ui-text-subtle" />
          <input
            type="text"
            placeholder="Buscar por nombre o enlace..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-ui-border bg-ui-surface pl-8 pr-3 py-1.5 text-sm text-ui-text placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent"
          />
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={28} className="animate-spin text-ui-text-subtle" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-ui-text-subtle">
          <QrCode size={32} className="mb-3 opacity-40" />
          <p className="text-sm">{search || moduleFilter !== 'all' ? 'Sin resultados' : 'Todavía no has generado códigos QR'}</p>
          {!search && moduleFilter === 'all' && (
            <p className="mt-1 text-sm">Crea uno con el botón de arriba o desde cualquier sección del panel</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((item) => (
            <div key={item.id} className="group flex gap-3 rounded-lg border border-ui-border bg-ui-surface p-3">
              <button
                type="button"
                onClick={() => setOpen(item)}
                title="Abrir código QR"
                className="shrink-0 rounded-lg bg-white p-1.5"
              >
                <img
                  src={item.url}
                  alt={`Código QR de ${item.label}`}
                  className="h-20 w-20 rounded object-contain"
                  onError={(e) => { (e.currentTarget.style.display = 'none'); }}
                />
              </button>
              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex items-start justify-between gap-2">
                  <p className="truncate font-medium text-ui-text">{item.label}</p>
                  <span className="shrink-0 rounded-full bg-ui-accent/10 px-2 py-0.5 text-xs font-medium text-ui-accent">
                    {moduleLabel(item.module)}
                  </span>
                </div>
                <a
                  href={item.targetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-flex items-center gap-1 truncate text-sm text-ui-text-subtle hover:text-ui-accent"
                >
                  <ExternalLink size={12} className="shrink-0" />
                  <span className="truncate">{item.targetUrl}</span>
                </a>
                <p className="mt-1 text-xs text-ui-text-subtle">{formatDate(item.createdAt)}</p>
                <div className="mt-auto flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setOpen(item)}
                    className="rounded-md border border-ui-border px-3 py-1.5 text-sm text-ui-text hover:bg-ui-text/[0.05]"
                  >
                    Abrir
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(item)}
                    title="Eliminar"
                    className="rounded-md border border-ui-border p-1.5 text-red-500 hover:bg-red-500/10"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Abrir / editar un QR existente */}
      {open && (
        <QrCodeModal
          value={open.targetUrl}
          label={open.label}
          module={open.module}
          refId={open.refId ?? undefined}
          initialStyle={parseStyle(open.styleJson)}
          onClose={() => { setOpen(null); load(); }}
        />
      )}

      {/* Crear un QR para cualquier enlace */}
      {createOpen && draft && (
        <Modal title="Nuevo código QR" onClose={() => setCreateOpen(false)}>
          <form
            className="space-y-4 pb-5"
            onSubmit={(e) => {
              e.preventDefault();
              if (!draft.value.trim()) return;
              setCreateOpen(false);
              setOpen({
                id: '', tenantId: '', module: 'custom', refId: null,
                label: draft.label.trim() || draft.value.trim(),
                targetUrl: draft.value.trim(), r2Key: '', url: '', styleJson: '{}',
                createdBy: '', createdAt: new Date(), updatedAt: null,
              });
            }}
          >
            <Field label="Nombre" hint="Para reconocerlo después en la lista.">
              <Input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} placeholder="Ej. Mostrador, Mesa 4, Folleto" />
            </Field>
            <Field label="Enlace" hint="La dirección a la que llevará el código al escanearlo.">
              <Input value={draft.value} onChange={(e) => setDraft({ ...draft, value: e.target.value })} placeholder="https://..." />
            </Field>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setCreateOpen(false)} className="rounded-md border border-ui-border px-4 py-2 text-sm text-ui-text hover:bg-ui-text/[0.05]">
                Cancelar
              </button>
              <button type="submit" className="rounded-md bg-ui-accent px-4 py-2 text-sm font-medium text-ui-bg hover:bg-ui-accent-hover">
                Generar
              </button>
            </div>
          </form>
        </Modal>
      )}
    </PageContainer>
  );
}
